import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/hooks/useAuthApi';
import { AlertCircle, LogIn, Loader2 } from 'lucide-react';
import AuthLayout from '@/components/auth/AuthLayout';
import InviteOnlyView from '@/components/auth/InviteOnlyView';
import { normalizeAuthErrorMessage, isInvitationRequiredError } from '@/lib/authErrors';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const Login = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showInviteOnly, setShowInviteOnly] = useState(false);
  const { signIn, reinitializeAuth } = useAuth();
  const navigate = useNavigate();

  // Extract invitation token from URL parameters
  const invitationToken = searchParams.get('token');
  const context = 'login'; // This is the login page, so context is always 'login'

  // Helper function to handle auth redirects
  const handleAuthRedirect = () => {
    // Store redirect intent in sessionStorage before auth
    const fromExtension = searchParams.get('from') === 'extension';
    const returnTo = searchParams.get('returnTo');

    if (fromExtension) {
      sessionStorage.setItem('authRedirect', 'extension');
      console.log('Stored extension redirect in sessionStorage');
    } else if (returnTo) {
      sessionStorage.setItem('authRedirect', returnTo);
      console.log('Stored returnTo redirect in sessionStorage:', returnTo);
    }
  };

  // Helper function to process redirect after successful auth
  const processRedirectAfterAuth = () => {
    const authRedirect = sessionStorage.getItem('authRedirect');
    sessionStorage.removeItem('authRedirect'); // Clean up

    if (authRedirect === 'extension') {
      console.log('Redirecting to extension auth success page');
      navigate('/extension/auth-success');
    } else if (authRedirect) {
      console.log('Redirecting to returnTo URL:', authRedirect);
      navigate(decodeURIComponent(authRedirect));
    } else {
      navigate('/');
    }
  };

  // Pre-populate email from URL params
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      handleAuthRedirect();
      await signIn(email, password);
      processRedirectAfterAuth();
    } catch (error: any) {
      setError(
        error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password'
          ? 'Invalid email or password'
          : error.code === 'auth/too-many-requests'
          ? 'Too many login attempts. Please try again later.'
          : error.message || 'An error occurred. Please try again.'
      );
      // Clean up sessionStorage on error
      sessionStorage.removeItem('authRedirect');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setIsLoading(true);

    try {
      // Get the OAuth URL from the backend using Google API Key and Secret
      const redirectUrl = `${window.location.origin}/auth/google/callback`;

      const response = await fetch(import.meta.env.VITE_API_BASE_URL+'/api/auth/google/url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          redirect_url: redirectUrl,
          invitation_token: invitationToken,
          context: context,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get OAuth URL');
      }

      const { url: authUrl } = await response.json();

      // Open popup for Google OAuth
      const popup = window.open(
        authUrl,
        'google_signin',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      );

      if (!popup) {
        throw new Error('Popup blocked. Please allow popups and try again.');
      }

      // Listen for the popup to close or receive a message
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          setError('Google sign-in was cancelled.');
          setIsLoading(false);
        }
      }, 1000);

      // Listen for message from popup (when redirect happens)
      const messageListener = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === 'GOOGLE_AUTH_SUCCESS') {
          clearInterval(checkClosed);
          popup.close();
          window.removeEventListener('message', messageListener);

          // Handle successful authentication
          handleGoogleAuthSuccess(event.data.code, event.data.state, redirectUrl);
        } else if (event.data.type === 'GOOGLE_AUTH_ERROR') {
          clearInterval(checkClosed);
          popup.close();
          window.removeEventListener('message', messageListener);

          setError(event.data.error || 'Google sign-in failed.');
          setIsLoading(false);
        }
      };

      window.addEventListener('message', messageListener);

      // Timeout after 5 minutes
      setTimeout(() => {
        clearInterval(checkClosed);
        window.removeEventListener('message', messageListener);
        if (!popup.closed) {
          popup.close();
        }
        setError('Google sign-in timed out. Please try again.');
        setIsLoading(false);
      }, 300000);

    } catch (error: any) {
      console.error('Google sign-in error:', error);
      const normalized = normalizeAuthErrorMessage(error?.message, 'Could not sign in with Google. Please try again.');
      if (isInvitationRequiredError(normalized)) {
        setShowInviteOnly(true);
      } else {
        setError(normalized);
      }
      setIsLoading(false);
    }
  };

  const handleGoogleAuthSuccess = async (authCode: string, state: string, redirectUri: string) => {
    try {
      // Handle the OAuth callback through the backend using Google API Key and Secret
      const response = await fetch(import.meta.env.VITE_API_BASE_URL+'/api/auth/google/callback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: authCode,
          state: state,
          context: context,
          redirect_uri: redirectUri,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to authenticate with Google');
      }

      // Handle successful authentication with the auth response
      handleAuthRedirect();

      // Store the authentication data and update user context
      if (data.access_token && data.user) {
        localStorage.setItem('hobbes_auth_token', data.access_token);

        // Reinitialize auth state to update the user context immediately
        await reinitializeAuth();

        // Navigate after successful authentication
        processRedirectAfterAuth();
      } else {
        throw new Error('Invalid authentication response');
      }
    } catch (error: any) {
      console.error('Google auth callback error:', error);
      const normalized = normalizeAuthErrorMessage(error?.message, 'Authentication failed. Please try again.');
      if (isInvitationRequiredError(normalized)) {
        setShowInviteOnly(true);
      } else {
        setError(normalized);
      }
      setIsLoading(false);
    }
  };

  const authInputClass = 'h-10 rounded-[10px] border-white/10 bg-transparent text-white text-base tracking-[-0.16px] placeholder:text-[#b2b2b2] focus-visible:ring-white/30 focus-visible:ring-offset-0';

  if (showInviteOnly) {
    return (
      <AuthLayout title="Invite Only" pageTitle="Invite Only" showTermsLinks={false}>
        <InviteOnlyView
          source="login_google_error"
          showSignInLink={false}
          onBack={() => setShowInviteOnly(false)}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Sign in to your account"
      pageTitle="Sign In"
      showInvitationLink={true}
      showCalLink={true}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <AnimatePresence mode="wait">
          {searchParams.get('expired') === 'true' && !error && (
            <motion.div
              key="expired"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-white/10 border border-white/10 text-[#e6e5e5] p-3 rounded-xl flex items-start gap-2"
            >
              <LogIn className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#a4a4a4]" />
              <span className="text-sm">Your session has ended. Please sign in to continue.</span>
            </motion.div>
          )}
          {error && (
            <motion.div
              key="error"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-red-500/20 border border-red-500/50 text-red-200 p-3 rounded-xl flex items-start gap-2"
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Email field */}
        <div className="flex flex-col gap-2">
          <label htmlFor="email" className="text-sm text-[#a4a4a4] tracking-[-0.14px]">
            Email
          </label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className={authInputClass}
          />
        </div>

        {/* Password field with forgot link below */}
        <div className="flex flex-col gap-2">
          <label htmlFor="password" className="text-sm text-[#a4a4a4] tracking-[-0.14px]">
            Password
          </label>
          <Input
            id="password"
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className={authInputClass}
          />
          <Link
            to="/forgot-password"
            className="text-sm text-[#a4a4a4] self-end tracking-[-0.14px] hover:text-white transition-colors"
          >
            Forgot password?
          </Link>
        </div>

        {/* Sign in button */}
        <Button
          type="submit"
          disabled={isLoading}
          className="w-full h-10 bg-white text-[#404040] font-medium rounded-[36px] hover:bg-white/90 text-base tracking-[-0.16px]"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Signing in...
            </span>
          ) : (
            'Sign In'
          )}
        </Button>

        {/* Divider */}
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-[#a4a4a4] tracking-[-0.12px]">Or continue with</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        {/* Google button */}
        <Button
          type="button"
          variant="outline"
          onClick={handleGoogleSignIn}
          disabled={isLoading}
          className="w-full h-10 bg-white/10 border-white/10 text-[#e6e5e5] font-medium rounded-[36px] hover:bg-white/20 hover:text-white text-base tracking-[-0.16px]"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
            <path d="M1 1h22v22H1z" fill="none" />
          </svg>
          Google
        </Button>
      </form>
    </AuthLayout>
  );
};

export default Login;
